const INTERACTION_EVENTS = ['mousedown', 'mousemove', 'scroll', 'keydown', 'touchstart'];

export class BrowserBotClassifier {
  constructor() {
    this.started = false;
    this.result = null;
    this.handleInteraction = this.handleInteraction.bind(this);
  }

  start() {
    this.stop();

    this.started = true;
    this.result = null;
    this.startedAt = performance.now();
    this.interactionCounts = {};
    this.trustedEvents = 0;
    this.untrustedEvents = 0;
    this.firstInteractionAt = null;
    this.lastInteractionAt = null;
    this.mouseDistancePixels = 0;
    this.lastMousePosition = null;

    INTERACTION_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, this.handleInteraction, { passive: true });
    });

    return this;
  }

  evaluate() {
    if (this.result) return this.result;

    const evaluatedAt = performance.now();
    const staticInfo = this.collectStaticInfo();
    this.stop();

    const interactionData = {
      event_types: Object.keys(this.interactionCounts || {}),
      event_counts: this.interactionCounts || {},
      total_events: Object.values(this.interactionCounts || {}).reduce((sum, count) => sum + count, 0),
      trusted_events: this.trustedEvents || 0,
      untrusted_events: this.untrustedEvents || 0,
      first_interaction_delay_ms: this.firstInteractionAt === null ? null : Math.round(this.firstInteractionAt - this.startedAt),
      interaction_span_ms: this.firstInteractionAt === null ? 0 : Math.round(this.lastInteractionAt - this.firstInteractionAt),
      mouse_distance_pixels: Math.round(this.mouseDistancePixels || 0),
      collection_duration_ms: this.startedAt ? Math.round(evaluatedAt - this.startedAt) : 0
    };
    const classification = this.classify(staticInfo, interactionData);

    this.result = {
      classifier_version: 2,
      automation_risk_score: classification.riskScore,
      signals: classification.signals,
      static_info: staticInfo,
      interaction_data: interactionData
    };

    return this.result;
  }

  stop() {
    if (!this.started) return;

    INTERACTION_EVENTS.forEach((eventName) => {
      document.removeEventListener(eventName, this.handleInteraction);
    });
    this.started = false;
  }

  handleInteraction(event) {
    const now = performance.now();
    this.interactionCounts[event.type] = (this.interactionCounts[event.type] || 0) + 1;
    this.firstInteractionAt = this.firstInteractionAt === null ? now : this.firstInteractionAt;
    this.lastInteractionAt = now;

    if (event.isTrusted) {
      this.trustedEvents += 1;
    } else {
      this.untrustedEvents += 1;
    }

    if (event.type === 'mousemove') {
      if (this.lastMousePosition) {
        this.mouseDistancePixels += Math.hypot(
          event.clientX - this.lastMousePosition.x,
          event.clientY - this.lastMousePosition.y
        );
      }
      this.lastMousePosition = { x: event.clientX, y: event.clientY };
    }
  }

  collectStaticInfo() {
    const userAgent = navigator.userAgent;

    return {
      webdriver: navigator.webdriver === true,
      user_agent: userAgent,
      user_agent_device_class: /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent) ? 'mobile' : 'desktop',
      platform: navigator.platform,
      languages: Array.from(navigator.languages || []),
      plugins_count: navigator.plugins ? navigator.plugins.length : null,
      mime_types_count: navigator.mimeTypes ? navigator.mimeTypes.length : null,
      hardware_concurrency: navigator.hardwareConcurrency || null,
      device_memory_gb: navigator.deviceMemory || null,
      max_touch_points: navigator.maxTouchPoints || 0,
      cookie_enabled: navigator.cookieEnabled,
      do_not_track: navigator.doNotTrack || null,
      screen_width: screen.width,
      screen_height: screen.height,
      color_depth: screen.colorDepth,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      outer_width: window.outerWidth,
      outer_height: window.outerHeight,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      visibility_state: document.visibilityState
    };
  }

  classify(staticInfo, interactionData) {
    let riskScore = 0.03;
    const signals = [];

    const addSignal = (name, weight) => {
      signals.push(name);
      riskScore += weight;
    };

    if (staticInfo.webdriver) addSignal('webdriver', 0.72);
    if (/HeadlessChrome|PhantomJS|SlimerJS/i.test(staticInfo.user_agent)) addSignal('headless_user_agent', 0.82);
    if (!staticInfo.languages.length) addSignal('missing_languages', 0.12);
    const outerWindowUnavailable = staticInfo.outer_width === 0 || staticInfo.outer_height === 0;
    const hasTrustedInteraction = interactionData.trusted_events > 0;

    if (outerWindowUnavailable) {
      let outerWindowRisk = 0;

      if (!hasTrustedInteraction) {
        outerWindowRisk = staticInfo.visibility_state === 'visible' ? 0.2 : 0.05;
        if (interactionData.total_events === 0) outerWindowRisk += 0.04;
      }

      addSignal('outer_window_unavailable', outerWindowRisk);
    }
    if (staticInfo.screen_width <= 0 || staticInfo.screen_height <= 0) addSignal('invalid_screen_dimensions', 0.15);
    if (
      (staticInfo.outer_width > 0 && staticInfo.viewport_width > staticInfo.outer_width + 1) ||
      (staticInfo.outer_height > 0 && staticInfo.viewport_height > staticInfo.outer_height + 1)
    ) {
      addSignal('viewport_exceeds_outer_window', 0.12);
    }
    if (staticInfo.viewport_width > staticInfo.screen_width + 1 || staticInfo.viewport_height > staticInfo.screen_height + 1) {
      addSignal('viewport_exceeds_screen', 0.1);
    }
    if (staticInfo.user_agent_device_class === 'mobile' && staticInfo.max_touch_points === 0) {
      addSignal('mobile_without_touch_support', 0.12);
    }
    if (/Android/i.test(staticInfo.user_agent) && !/Linux|Android/i.test(staticInfo.platform)) {
      addSignal('android_platform_mismatch', 0.1);
    }
    if (/iPhone|iPad|iPod/i.test(staticInfo.user_agent) && !/iPhone|iPad|iPod|Mac/i.test(staticInfo.platform)) {
      addSignal('ios_platform_mismatch', 0.1);
    }
    if (interactionData.event_types.includes('touchstart') && staticInfo.max_touch_points === 0) {
      addSignal('touch_event_without_touch_support', 0.2);
    }
    if (interactionData.untrusted_events > 0) addSignal('untrusted_interactions', 0.18);
    if (interactionData.collection_duration_ms >= 5000 && interactionData.total_events === 0) {
      addSignal('no_interaction', 0.08);
    }

    return {
      riskScore: Number(Math.min(0.99, Math.max(0.01, riskScore)).toFixed(3)),
      signals: signals
    };
  }
}

export const browserBotClassifier = new BrowserBotClassifier();